<svelte:options accessors />

<script context="module">
  export const DEFAULT_DURATION = 200;

  // 惯性滑动思路:
  // 在手指离开屏幕时，如果和上一次 move 时的间隔小于 `MOMENTUM_LIMIT_TIME` 且 move
  // 距离大于 `MOMENTUM_LIMIT_DISTANCE` 时，执行惯性滑动
  export const MOMENTUM_LIMIT_TIME = 300;
  export const MOMENTUM_LIMIT_DISTANCE = 15;

  export const PICKER_COLUMN_EVENTS = {
    change: 'change',
    clickOption: 'clickOption',
  };

  // use standard WheelEvent:
  // https://developer.mozilla.org/en-US/docs/Web/API/WheelEvent
  const supportMousewheel = typeof window !== 'undefined' && 'onwheel' in window;
</script>

<script lang="ts">
  import { createEventDispatcher, onMount } from 'svelte';

  import { preventDefault } from '@/comps-sdk/utils/dom';
  import { clamp } from '@/comps-sdk/utils/format';

  import { getElementTranslateY } from '../util';
  import { useTouch } from './use-touch';

  export let valueKey = '';
  export let readonly = false;
  export let allowHtml = true;
  export let className = '';
  export let itemHeight = 0;
  export let defaultIndex = 0;
  export let swipeDuration: Number = 0;
  export let visibleItemCount = 0;
  export let initialOptions: Array<any> = [];

  const dispatch = createEventDispatcher();
  const touch = useTouch();

  let offset = 0;
  let duration = 0;
  let options = initialOptions.slice(0);
  let currentIndex = defaultIndex;

  let root: HTMLElement;
  let wrapper: HTMLElement;
  let transitionEndTrigger: any = null;
  let moving = false;

  let startOffset = 0;
  let touchStartTime = 0;
  let momentumOffset = 0;

  $: count = options.length;
  $: baseOffset = (itemHeight * (visibleItemCount - 1)) / 2;
  $: {
    setIndex(defaultIndex);
  }

  export const setOptions = (newOptions: any) => {
    if (JSON.stringify(options) !== JSON.stringify(newOptions)) {
      options = newOptions.slice(0);
      setIndex(defaultIndex);
    }
  };

  let wrapperStyle = {
    transform: `translate3d(0, ${offset + baseOffset}px, 0)`,
    transitionDuration: `${duration}ms`,
    transitionProperty: duration ? 'all' : 'none',
  };

  $: {
    wrapperStyle = {
      transform: `translate3d(0, ${offset + baseOffset}px, 0)`,
      transitionDuration: `${duration}ms`,
      transitionProperty: duration ? 'all' : 'none',
    };
  }

  const setIndex = (i: number, isEmitChange = false) => {
    const index = adjustIndex(i) || 0;
    const offsetTemp = -index * itemHeight;

    const trigger = () => {
      if (index !== currentIndex) {
        currentIndex = index;
        if (isEmitChange) {
          dispatch(PICKER_COLUMN_EVENTS.change, index);
        }
      }
    };

    // trigger the change event after transitionend when moving
    if (moving && offsetTemp !== offset) {
      transitionEndTrigger = trigger;
    } else {
      trigger();
    }

    offset = offsetTemp;
  };

  export const setValue = (value: any) => {
    for (let i = 0; i < options.length; i++) {
      if (getOptionText(options[i]) === value) {
        return setIndex(i);
      }
    }
  };

  export const getValue = () => options[currentIndex];

  export const getCurrentIndex = () => currentIndex;

  const adjustIndex = (i: number) => {
    let index = i;
    index = clamp(index, 0, count);

    for (let i = index; i < count; i++) {
      if (!options[i]?.disabled) return i;
    }

    for (let i = index - 1; i >= 0; i--) {
      if (!options[i]?.disabled) return i;
    }
  };

  const getOptionText = (option: any) => {
    if (typeof option === 'object' && valueKey in option) {
      return option[valueKey];
    }
    return option;
  };

  const onTouchStart = (event: TouchEvent) => {
    if (readonly) {
      return;
    }

    touch.start(event);

    if (moving) {
      const translateY = getElementTranslateY(wrapper);
      offset = Math.min(0, translateY - baseOffset);
      startOffset = offset;
    } else {
      startOffset = offset;
    }

    duration = 0;
    transitionEndTrigger = null;
    touchStartTime = Date.now();
    momentumOffset = startOffset;
  };

  const onTouchMove = (event: TouchEvent) => {
    if (readonly) {
      return;
    }

    touch.move(event);

    if (touch.isVertical()) {
      moving = true;
      if (event.cancelable) {
        event.preventDefault();
      }
    }

    offset = clamp(startOffset + touch.data.deltaY, -(count * itemHeight), itemHeight);

    const now = Date.now();
    if (now - touchStartTime > MOMENTUM_LIMIT_TIME) {
      touchStartTime = now;
      momentumOffset = offset;
    }
  };

  const onTouchEnd = () => {
    if (readonly) {
      return;
    }

    const distance = offset - momentumOffset;
    const innerDuration = Date.now() - touchStartTime;
    const allowMomentum = innerDuration < MOMENTUM_LIMIT_TIME && Math.abs(distance) > MOMENTUM_LIMIT_DISTANCE;

    if (allowMomentum) {
      momentum(distance, innerDuration);
      return;
    }

    const index = getIndexByOffset(offset);
    duration = DEFAULT_DURATION;
    setIndex(index, true);

    // compatible with desktop scenario
    // use setTimeout to skip the click event Emitted after touchstart
    setTimeout(() => {
      moving = false;
    }, 0);
  };

  let mousewheelTimer: any;
  const onMouseWheel = (event: WheelEvent) => {
    if (readonly) {
      return;
    }
    preventDefault(event, true);
    // simply combine touchstart and touchmove
    const translateY = getElementTranslateY(wrapper);
    startOffset = Math.min(0, translateY - baseOffset);
    momentumOffset = startOffset;
    transitionEndTrigger = null;

    // directly use deltaY, see https://caniuse.com/?search=deltaY
    // use deltaY to detect direction for not special setting device
    // https://developer.mozilla.org/en-US/docs/Web/API/Element/wheel_event
    const { deltaY } = event;
    if (startOffset === 0 && deltaY < 0) {
      return;
    }

    // Calculate the offset based on itemHeight
    const itemOffset = itemHeight * (deltaY > 0 ? -1 : 1);
    offset = clamp(startOffset + itemOffset, -(count * itemHeight), itemHeight);

    if (mousewheelTimer) {
      clearTimeout(mousewheelTimer);
    }

    mousewheelTimer = setTimeout(() => {
      onTouchEnd();
      touchStartTime = 0;
    }, MOMENTUM_LIMIT_TIME);
  };

  const momentum = (distance: number, customDuration: number) => {
    const speed = Math.abs(distance / customDuration);

    const newDistance = offset + (speed / 0.003) * (distance < 0 ? -1 : 1);

    const index = getIndexByOffset(newDistance);
    duration = +swipeDuration;
    setIndex(index, true);
  };

  export const stopMomentum = () => {
    moving = false;
    duration = 0;
    if (transitionEndTrigger) {
      transitionEndTrigger();
      transitionEndTrigger = null;
    }
  };

  const getIndexByOffset = (offset: number) => clamp(Math.round(-offset / itemHeight), 0, count - 1);

  const onClickItem = (index: number) => {
    if (moving || readonly) {
      return;
    }

    transitionEndTrigger = null;
    duration = DEFAULT_DURATION;
    setIndex(index, true);
  };

  onMount(() => {
    wrapper.addEventListener('transitionend', stopMomentum);

    if (supportMousewheel) {
      wrapper.addEventListener('wheel', onMouseWheel, false);
    }
    return () => {
      wrapper.removeEventListener('wheel', onMouseWheel);
      wrapper.removeEventListener('transitionend', stopMomentum);
    };
  });

  setIndex(currentIndex);
</script>

<div
  bind:this={root}
  class={`iwan-picker-column ${className}`}
  on:touchstart={onTouchStart}
  on:touchmove={onTouchMove}
  on:touchend={onTouchEnd}
  on:touchcancel={onTouchEnd}
>
  <ul
    bind:this={wrapper}
    class="iwan-picker-column-wrapper"
    style:transform={wrapperStyle.transform}
    style:transition-duration={wrapperStyle.transitionDuration}
    style:transition-property={wrapperStyle.transitionProperty}
  >
    {#each options as option, index}
      <!-- svelte-ignore a11y-no-noninteractive-element-interactions a11y-click-events-have-key-events -->
      <li
        class="iwan-picker-column-item"
        class:disabled={option.disabled}
        class:selected={index === currentIndex}
        style:height={`${itemHeight}px`}
        on:click={() => {
          onClickItem(index);
        }}
      >
        <div>
          {#if allowHtml}
            <!-- 这里渲染自定义的下拉选项框，允许用 html -->
            <!--eslint-disable-next-line-->
            {@html getOptionText(option)}
          {:else}
            {getOptionText(option)}
          {/if}
        </div>
      </li>
    {/each}
  </ul>
</div>

<style lang="scss">
  .iwan-picker-column {
    flex: 1;
    overflow: hidden;
    font-size: 16;
    .iwan-picker-column-wrapper {
      margin: 0;
      padding: 0;
      list-style: none;
      transition-timing-function: cubic-bezier(0.23, 1, 0.68, 1);
      .iwan-picker-column-item {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0 4px;
        color: #000000;
        div {
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }
      }
    }
  }
</style>
